import { useEffect, useState } from "react";
import PasswordDialog from "./PasswordDialog";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard, Users, FolderKanban, Clock, Receipt, CheckSquare, UserCog, BarChart3, LogOut,
  ArrowUpRight, CalendarDays, Wallet, PartyPopper, FileText, Database, Activity, Coffee, ScrollText,
  LineChart, HardDriveDownload, ListChecks, Newspaper, TrendingUp, BookOpen, Bot, Calculator,
  Building2, Briefcase, Sparkles, Shield, FileSpreadsheet, Globe, Image, KeyRound,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const SECTIONS = [
  {
    id: "office",
    label: "Office Admin",
    icon: Building2,
    items: [
      { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["partner","admin","manager","employee","accounts","client"] },
      { to: "/portal/clients", label: "Clients", icon: Users, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/projects", label: "Projects", icon: FolderKanban, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/tasks", label: "Tasks (in Projects)", icon: ListChecks, roles: ["_none"] },
      { to: "/portal/timesheet", label: "Timesheet", icon: Clock, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/status", label: "Status", icon: Activity, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/reimbursements", label: "Reimbursements", icon: Receipt, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/leave", label: "Leave", icon: CalendarDays, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/comp-off", label: "Comp-off & Permissions", icon: Coffee, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/hr-approvals", label: "Leave & Time Approvals", icon: CheckSquare, roles: ["partner","admin"] },
      { to: "/portal/approvals", label: "Approvals", icon: CheckSquare, roles: ["partner","admin","manager","accounts"] },
      { to: "/portal/billing", label: "Billing", icon: ScrollText, roles: ["partner","admin","manager","accounts"] },
      { to: "/portal/laptop-tracker", label: "Laptop Tracker", icon: Database, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/finance-io", label: "Bulk Exports/Imports", icon: FileSpreadsheet, roles: ["_none"] },
      { to: "/portal/website", label: "Website & Enquiries", icon: Globe, roles: ["partner","admin","manager"] },
      { to: "/portal/people", label: "People", icon: UserCog, roles: ["partner","admin"] },
      { to: "/portal/access", label: "Access Mgmt", icon: Shield, roles: ["partner"] },
      { to: "/portal/holidays", label: "Holidays & Policies", icon: PartyPopper, roles: ["partner","admin","manager","employee","accounts"] },
      { to: "/portal/policies", label: "Policies (merged)", icon: FileText, roles: ["_none"] },
      { to: "/portal/hr", label: "HR & Payroll Desk", icon: Wallet, roles: ["partner","admin","accounts"] },
      { to: "/portal/payroll", label: "Payroll", icon: Wallet, roles: ["partner","admin"] },
      { to: "/portal/reports", label: "Reports", icon: BarChart3, roles: ["partner","admin","manager"] },
      { to: "/portal/mis", label: "MIS", icon: LineChart, roles: ["partner","admin"] },
      { to: "/portal/backup", label: "Backup", icon: HardDriveDownload, roles: ["partner","admin"] },
      { to: "/portal/data-management", label: "Data Management", icon: Database, roles: ["_none"] },
    ],
  },
  {
    id: "kip",
    label: "Knowledge Intelligence",
    icon: Sparkles,
    requires: "kip",
    items: [
      { to: "/portal/kip", label: "KIP Dashboard", icon: LayoutDashboard, end: true, kip_section: null },
      { to: "/portal/kip/updates", label: "Regulatory Updates", icon: Newspaper, kip_section: "updates" },
      { to: "/portal/kip/documents", label: "Knowledge Repository", icon: BookOpen, kip_section: "documents" },
      { to: "/portal/kip/markets", label: "Markets & Companies", icon: TrendingUp, kip_section: "_removed_markets" },
      { to: "/portal/kip/centre", label: "Financial Centre", icon: LineChart, kip_section: "_removed_centre" },
      { to: "/portal/kip/assistant", label: "AI Assistant", icon: Bot, kip_section: "assistant" },
      { to: "/portal/kip/bulletin", label: "Bulletin Builder", icon: FileText, kip_section: "bulletin" },
    ],
  },
  {
    id: "tools",
    label: "Audit & Accounting Tools",
    icon: Briefcase,
    requires: "tools",
    items: [
      { to: "/portal/tools", label: "Tool Catalogue", icon: Calculator, end: true },
      { to: "/portal/tools/history", label: "My Runs", icon: Clock },
    ],
  },
];

function hasSection(user, section) {
  if (!section) return true;
  if (user?.role === "partner" || user?.role === "admin") return true;
  const perms = user?.permissions || {};
  if (section.id === "kip") return !!(perms.kip || {}).enabled;
  if (section.id === "tools") return !!(perms.tools || {}).enabled;
  return true;
}

function hasItem(user, section, item) {
  // "_none" always hides regardless of role (used to soft-retire nav items)
  if (item.roles && item.roles.length === 1 && item.roles[0] === "_none") return false;
  if (section.id === "kip" && (item.kip_section || "").startsWith("_removed_")) return false;
  if (user?.role === "partner" || user?.role === "admin") return true;
  if (item.roles && !item.roles.includes(user?.role)) return false;
  if (section.id === "kip" && item.kip_section) {
    const sections = ((user?.permissions || {}).kip || {}).sections || [];
    return sections.includes(item.kip_section);
  }
  return true;
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/login"); };
  const [pwOpen, setPwOpen] = useState(false);
  useEffect(() => { document.title = "VCAJ Portal"; }, []);

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="portal-layout">
      <aside className="w-64 bg-white border-r border-slate-200 hidden md:flex flex-col" data-testid="sidebar">
        <Link to="/" className="h-16 px-6 flex items-center gap-3 border-b border-slate-200">
          <div className="w-9 h-9 rounded-sm bg-slate-900 text-white flex items-center justify-center font-sans text-lg">V</div>
          <div className="leading-tight">
            <div className="font-sans text-base">V C A J Portal</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Internal</div>
          </div>
        </Link>
        <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
          {SECTIONS.map((sec) => {
            if (!hasSection(user, sec)) return null;
            const items = sec.items.filter((n) => hasItem(user, sec, n));
            if (!items.length) return null;
            const SecIcon = sec.icon;
            return (
              <div key={sec.id} data-testid={`section-${sec.id}`}>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium flex items-center gap-2">
                  <SecIcon className="h-3 w-3" /> {sec.label}
                </div>
                <div className="space-y-1">
                  {items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end}
                      className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                      data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                      <n.icon className="h-4 w-4"/> {n.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2">
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="sidebar-user-role">{user?.role}</div>
          </div>
          <Button onClick={() => setPwOpen(true)} variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 rounded-sm" data-testid="change-password-btn">
            <KeyRound className="h-4 w-4 mr-2"/> Change password
          </Button>
          <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
          <Button onClick={handleLogout} variant="ghost" className="w-full justify-start text-slate-600 hover:text-slate-900 rounded-sm" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2"/> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">V C A J & Associates LLP · Internal Portal</div>
          <Link to="/" className="text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">Public Site <ArrowUpRight className="h-3 w-3"/></Link>
        </header>
        <div className="flex-1 p-6 lg:p-8">
          <Outlet/>
        </div>
      </main>
    </div>
  );
}
